import { Box, Alert } from '@mui/material'
import { FunctionComponent, useEffect, useState, useRef, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import FileUploadService from '../../../../../../shared/services/file-upload/file-upload.service'
import { IFileUpload } from '../../../../../../shared/services/file-upload/file-upload.model'
import { LoadingSpinner } from '../../../../../presentation-components/display-elements-components/loading-spinner'
import { ImportProcessingIndicator } from '../../../../../presentation-components/display-elements-components/import-processing-indicator'
import { ImportStepHeader } from './import-step-header'

export const FileCommitProgress: FunctionComponent<{
  /** Embedded in the wizard: poll this upload rather than the route param. */
  fileUploadId?: string
  /** Embedded in the wizard: the wizard renders its own step header above this content. */
  hideStepHeader?: boolean
  /**
   * Embedded in the wizard: called once the commit has finished, after a short success
   * beat, instead of the standalone page's redirect.
   */
  onComplete?: (fileUpload: IFileUpload) => void
}> = ({ fileUploadId: fileUploadIdProp, hideStepHeader = false, onComplete }) => {
  const { fileUploadId: fileUploadIdFromRoute } = useParams<{ fileUploadId: string }>()
  const fileUploadId = fileUploadIdProp || fileUploadIdFromRoute
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const fileUploadServiceRef = useRef(new FileUploadService())
  const [fileUpload, setFileUpload] = useState<IFileUpload | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isCompleted, setIsCompleted] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const redirectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // The wizard's callback, held in a ref so the polling and completion effects do not
  // restart every time the parent re-renders with a fresh arrow function.
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const fetchProgress = useCallback(async () => {
    if (!fileUploadId) {
      setError('File upload ID is missing')
      setIsLoading(false)
      return
    }
    try {
      const data = await fileUploadServiceRef.current.checkFileUploadCommitProgress(fileUploadId)
      setFileUpload(data)
      setIsLoading(false)
      setError(null)
      // Check if import has failed
      if (data.lineImportStatus === 'FAILED' || data.lineCommitStatus === 'NOT_REQUESTED' || data.lineCommitStatus === 'FAILED') {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
        // Show why rather than bouncing to the history list with nothing. The reasons come from the
        // rows that could not be saved; failureReason covers a run that failed before reaching them.
        const reason = (data.failureReasons && data.failureReasons.length > 0)
          ? data.failureReasons.join(' ')
          : data.failureReason
        if (reason) {
          setError(reason)
          return
        }
        // No recorded reason. Embedded, stay put with an honest line rather than yanking the
        // whole wizard off to the history list; standalone keeps its original redirect.
        if (onCompleteRef.current) {
          setError('The import could not be completed. You can check the upload in Import History.')
          return
        }
        navigate('/settings/data-upload/import-history')
        return
      }
      // Check if commit is completed
      const toCommitCount = (data as any).toCommitCount ?? 0
      const numberOfRecords = data.numberOfRecords ?? 0
      const alreadyCommittedCount = (data as any).alreadyCommittedCount ?? 0
      const completedCount = numberOfRecords - alreadyCommittedCount

      // Mark as completed if backend marked it completed, or if the completed count reaches the target
      const shouldComplete = data.lineCommitStatus === 'COMPLETED'
        || (toCommitCount > 0 && completedCount >= toCommitCount)

      if (shouldComplete) {
        setIsCompleted(true)
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
      }
    } catch (err: any) {
      console.error('Error fetching commit progress:', err)
      setError(err?.response?.data?.message || 'Failed to fetch commit progress')
      setIsLoading(false)
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [fileUploadId, navigate])

  useEffect(() => {
    // Clear any existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    // Initial fetch
    fetchProgress()
    // Set up polling every 5 seconds only if not completed
    if (!isCompleted) {
      pollingIntervalRef.current = setInterval(() => {
        fetchProgress()
      }, 5000)
    }
    // Cleanup on unmount or when completed
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [fileUploadId, fetchProgress, isCompleted])

  // On completion: hold the success state briefly, then hand over. Embedded in the wizard
  // that is a callback and the step advances in place; standalone it is the original redirect.
  useEffect(() => {
    if (isCompleted && fileUploadId && !isProcessing) {
      redirectTimeoutRef.current = setTimeout(() => {
        setIsProcessing(true)
        if (onCompleteRef.current) {
          if (fileUpload) {
            onCompleteRef.current(fileUpload)
          }
          return
        }
        // Navigate after showing processing message
        setTimeout(() => {
          const returnTo = searchParams.get('returnTo')
          const successStep = searchParams.get('successStep')
          const detailId = searchParams.get('id')
          const detailUploadId = searchParams.get('uploadId')

          if (returnTo === 'wizard') {
            // A link from the era when the wizard round-tripped through this page. Hand back
            // to the wizard's final step; the JSON importResult payload is gone, so the wizard
            // reports a generic success line there.
            navigate(`/settings/data-upload/import-data?fileUploadId=${fileUploadId}&step=${successStep || '3'}`)
          } else if (returnTo === 'detail' && detailId && detailUploadId) {
            // Navigate back to detail page
            navigate(`/settings/data-upload/import-history/${detailId}/${detailUploadId}?tabId=1`)
          } else {
            // Default navigation
            navigate(`/settings/data-upload/import-data?fileUploadId=${fileUploadId}&step=2`)
          }
        }, 100)
      }, onCompleteRef.current ? 1500 : 2000)
    }

    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current)
        redirectTimeoutRef.current = null
      }
    }
  }, [isCompleted, fileUploadId, isProcessing, navigate, searchParams, fileUpload])

  // Calculate progress percentage
  const getProgressPercentage = () => {
    if (!fileUpload) {
      return 0
    }
    const toCommitCount = (fileUpload as any).toCommitCount ?? 0
    if (toCommitCount === 0) {
      return fileUpload.lineCommitStatus === 'COMPLETED' ? 100 : 0
    }
    const numberOfRecords = fileUpload.numberOfRecords ?? 0
    const alreadyCommittedCount = (fileUpload as any).alreadyCommittedCount ?? 0
    const completedCount = numberOfRecords - alreadyCommittedCount
    const percentage = (completedCount / toCommitCount) * 100
    return Math.min(percentage, 100)
  }

  const progressPercentage = getProgressPercentage()
  const totalSteps = fileUpload?.linkToExistingEmission ? 5 : 4
  const currentStep = fileUpload?.linkToExistingEmission ? 4 : 3

  if (isLoading && !fileUpload) {
    return (
      <Box sx={{ py: 1 }}>
        {!hideStepHeader && <ImportStepHeader currentStep={currentStep} totalSteps={totalSteps} />}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <LoadingSpinner />
        </Box>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ py: 1 }}>
        {!hideStepHeader && <ImportStepHeader currentStep={currentStep} totalSteps={totalSteps} />}
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    )
  }

  if (!fileUpload) {
    return (
      <Box sx={{ py: 1 }}>
        {!hideStepHeader && <ImportStepHeader currentStep={currentStep} totalSteps={totalSteps} />}
        <Alert severity="warning">
          File upload not found
        </Alert>
      </Box>
    )
  }

  const toCommitCount = (fileUpload as any).toCommitCount ?? 0
  const numberOfRecords = fileUpload.numberOfRecords ?? 0
  const alreadyCommittedCount = (fileUpload as any).alreadyCommittedCount ?? 0
  const completedCount = numberOfRecords - alreadyCommittedCount

  return (
    <Box sx={{ py: 1 }}>
      {!hideStepHeader && <ImportStepHeader currentStep={currentStep} totalSteps={totalSteps} />}
      <Box sx={{ mt: 2 }}>
        <ImportProcessingIndicator
          percent={progressPercentage}
          rowsDone={completedCount}
          rowsTotal={toCommitCount || undefined}
          complete={isCompleted}
          indeterminate={!isCompleted && !toCommitCount}
          scale={1.1}
          maxWidth={880}
          title="Importing your data"
          subtitle="Bringing your records into the system"
          steps={[
            { short: 'Importing', long: 'Importing your records' },
            { short: 'Calculating', long: 'Calculating emissions' },
            { short: 'Finalising', long: 'Finalising' },
          ]}
          messages={[
            'Importing your records…',
            'Calculating emissions…',
            'Applying emission factors…',
            'Almost there…',
          ]}
          footerText="Large files can take a moment. You can navigate away and check back later; your import keeps running."
        />
      </Box>
    </Box>
  )
}

